import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`

const zoomIn = keyframes`
    from { opacity: 0; transform: scale(0.96); }
    to { opacity: 1; transform: scale(1); }
`

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 9999;
    background: rgba(0, 0, 0, 0.85);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    overflow-y: auto;
    animation: ${fadeIn} 0.2s ease-out both;
`

const Box = styled.div`
    position: relative;
    width: 100%;
    max-width: 1100px;
    background: #111;
    border: 1px solid #CD0FE8;
    padding: 40px 12px 12px;
    animation: ${zoomIn} 0.25s ease-out both;
    @media (min-width: 996px) {
        padding: 48px 20px 20px;
    }
`

const Header = styled.div`
    position: absolute;
    top: 0;
    left: 12px;
    right: 0;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: #fff;
    font-size: 14px;
    text-transform: uppercase;
    & span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    @media (min-width: 996px) {
        left: 20px;
        height: 48px;
    }
`

const Fechar = styled.button`
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    background: transparent;
    border: 0;
    color: #fff;
    font-size: 28px;
    line-height: 1;
    transition: 0.3s linear;
    &:hover {
        color: #CD0FE8;
    }
`

const Conteudo = styled.div`
    position: relative;
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
`

const Carregando = styled.span`
    position: absolute;
    color: #fff;
    font-size: 14px;
    opacity: 0.6;
`

const Imagem = styled.img`
    display: block;
    width: 100%;
    height: auto;
    max-height: 80vh;
    object-fit: contain;
    opacity: ${(props) => (props.$carregada ? 1 : 0)};
    transition: opacity 0.2s ease-out;
`

const Pdf = styled.iframe`
    display: block;
    width: 100%;
    height: 80vh;
    border: 0;
    background: #fff;
`

const AbrirArquivo = styled.a`
    display: inline-block;
    margin-top: 12px;
    color: #fff;
    font-size: 14px;
    &:hover {
        color: #CD0FE8;
    }
`

function ImagemCertificado({ certificado }) {
    const [carregada, setCarregada] = useState(false);

    return (
        <Conteudo>
            {!carregada && <Carregando>Carregando...</Carregando>}
            <Imagem
                src={certificado.arquivo}
                alt={`Certificado ${certificado.titulo} - ${certificado.instituicao}`}
                $carregada={carregada}
                onLoad={() => setCarregada(true)}
            />
        </Conteudo>
    );
}

function CertificadoModal({ certificado, onClose }) {
    useEffect(() => {
        if (!certificado) return;

        const onKey = (e) => e.key === 'Escape' && onClose();
        document.addEventListener('keydown', onKey);

        // compensa a largura da scrollbar para a página não "pular" ao travar o scroll
        const larguraScrollbar = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = `${larguraScrollbar}px`;

        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        };
    }, [certificado, onClose]);

    if (!certificado) return null;

    const isPdf = certificado.arquivo.endsWith('.pdf');

    return createPortal(
        <Overlay onClick={onClose}>
            <Box
                role="dialog"
                aria-modal="true"
                aria-label={certificado.titulo}
                onClick={(e) => e.stopPropagation()}
            >
                <Header>
                    <span>{certificado.titulo}</span>
                    <Fechar type="button" onClick={onClose} aria-label="Fechar">&times;</Fechar>
                </Header>
                {isPdf ? (
                    <>
                        <Pdf src={certificado.arquivo} title={certificado.titulo} />
                        <AbrirArquivo href={certificado.arquivo} target="_blank" rel="noreferrer">
                            Abrir PDF em nova aba
                        </AbrirArquivo>
                    </>
                ) : (
                    <ImagemCertificado key={certificado.arquivo} certificado={certificado} />
                )}
            </Box>
        </Overlay>,
        document.body
    );
}

export default CertificadoModal;
